/**
 * 확인 다이얼로그 컴포넌트
 * - window.confirm() 대체용 스타일된 모달
 * - Esc 키 / 오버레이 클릭으로 닫기
 * - variant='destructive' 시 확인 버튼 빨간색
 */
import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { dialogVariants, useSnapVariants } from '../../lib/motion';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = '확인',
  cancelText = '취소',
  variant = 'default',
}) => {
  const confirmRef = React.useRef<HTMLButtonElement>(null);
  const snapDialog = useSnapVariants(dialogVariants);

  // Esc 키로 닫기
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // 열릴 때 확인 버튼에 포커스
  React.useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => confirmRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-[calc(100vw-16px)] xs:max-w-sm mx-4 p-6"
            variants={snapDialog}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 제목 */}
            <h2
              id="confirm-dialog-title"
              className="text-base font-semibold text-gray-800 mb-2"
            >
              {title}
            </h2>

            {/* 설명 */}
            <p
              id="confirm-dialog-description"
              className="text-sm text-gray-600 mb-6 leading-relaxed"
            >
              {description}
            </p>

            {/* 버튼 */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose} className="min-h-[44px]">
                {cancelText}
              </Button>
              <Button
                ref={confirmRef}
                variant={variant === 'destructive' ? 'destructive' : 'default'}
                size="sm"
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className={cn(
                  'min-h-[44px]',
                  variant === 'destructive' && 'bg-red-500 hover:bg-red-600 text-white'
                )}
              >
                {confirmText}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmDialog;
